import { toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  gateAccountCategory,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/wallet/withdrawals',
    query: {
      limit: config.limit.toString(),
      ...(from ? { from: Math.floor(from / 1000).toString() } : {}),
      ...(to ? { to: Math.ceil(to / 1000).toString() } : {}),
    },
    responseSchema,
  })

  // Sort by timestamp in ascending order (convert string timestamp to number)
  const sortedWithdrawals = response.sort(
    (a, b) => Number.parseInt(a.timestamp) - Number.parseInt(b.timestamp),
  )

  // Process data
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedWithdrawals) {
    // Skip canceled, failed, or invalid withdrawals
    if (
      item.status === 'CANCEL' ||
      item.status === 'FAIL' ||
      item.status === 'INVALID'
    ) {
      continue
    }

    // Stop processing if withdrawal is not done (pending statuses)
    if (
      item.status === 'REQUEST' ||
      item.status === 'MANUAL' ||
      item.status === 'BCODE' ||
      item.status === 'EXTPEND' ||
      item.status === 'VERIFY' ||
      item.status === 'PROCES' ||
      item.status === 'PEND' ||
      item.status === 'DMOVE' ||
      item.status === 'REVIEW'
    ) {
      stopped = true
      break
    }

    // Convert timestamp from seconds to milliseconds
    const timestamp = Number.parseInt(item.timestamp) * 1000

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    // Create fee action if fee > 0
    if (item.fee.value > 0n) {
      const asset = getAsset(item.currency)
      if (!asset) {
        throw new AssetNotFoundException(item.currency)
      }

      actions.push({
        comment: `Withdraw ${toStringBigDecimal(item.amount)} [${item.currency}] | network: ${item.chain} | tx: ${item.txid}`,
        evidence: 'system-rule',
        order: 0,
        source: `${gateAccountCategory}_${config.type}_${item.id}`,
        type: 'fee',
        timestamp,
        transfers: [
          {
            asset,
            amount: item.fee,
            direction: 'out',
          },
        ],
      })
    }

    // Create EVM source if applicable
    if (item.chain && item.txid) {
      const evmChainId = networkEvmChainIds[item.chain]
      if (evmChainId && isHex(item.txid)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.txid,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.chain === solanaNetwork && item.txid) {
      sources.push(createSolanaActionSource({ signature: item.txid }))
    }
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
